import type { Event } from "../../../../../../../../api/useEvents";
import { isFailureValueInFaultEventOccurrencePeriods } from "diagnostics-domain/faultEvent";

type HasId<Id extends string | number = string> = { id?: Id };

export function filterSeriesByEventOccurrence<T extends HasId>({
  series,
  event,
  date,
  displayAllFaults,
}: {
  series: T[];
  event: Event;
  date: number;
  displayAllFaults: boolean;
}): T[] {
  if (event.type !== "DIAGNOSED" || displayAllFaults) return series;

  return series.filter(serie => {
    const failureName = event.metadata.faults.find(fault =>
      String(serie.id ?? "").endsWith(fault.name),
    )?.name;
    return (
      failureName === undefined ||
      isFailureValueInFaultEventOccurrencePeriods(event, {
        projectId: event.projectId,
        failureName,
        date,
      })
    );
  });
}

export function filterSeriesByFocus<T extends HasId>({
  series,
  event,
  displayAllFaults,
  failureNames,
  getEventVisibleSeries,
}: {
  series: T[];
  event: Event;
  displayAllFaults: boolean;
  failureNames: string[];
  getEventVisibleSeries: (event: Event) => { included: string[]; excluded: string[] };
}) {
  if (displayAllFaults) return series;

  const { included, excluded } = getEventVisibleSeries(event);

  return series.filter(s =>
    s.id ? isFocusedFaultSeries(s.id, included, excluded, failureNames) : false,
  );
}

export const isFocusedFaultSeries = (
  serieId: string,
  includedSeries: string[],
  excludedSerieIds: string[],
  faultNames: string[],
) => {
  if (excludedSerieIds.some(excludeSerieId => serieId.startsWith(excludeSerieId))) return false;
  const isFaultSerie = includedSeries.some(faultIdSerie => serieId.startsWith(faultIdSerie));
  const isMatchFaultSerie =
    (isFaultSerie && faultNames.some(faultName => serieId.endsWith(faultName))) || !isFaultSerie;
  return isMatchFaultSerie;
};
